import { cn } from '@/helpers/ui-utils';

export interface HelperTextProps {
  /**
   * Unique identifier for the helper text. Used to relate ID with aria-describedby for the element.
   */
  id: string;

  /**
   * The content to display as helper text. Can be text, JSX elements, or components.
   */
  children: React.ReactNode;

  /**
   * Whether the helper text represents an invalid/error state. Applies warning styling when true.
   * @default false
   */
  invalid?: boolean;

  /**
   * Additional CSS classes to apply to the helper text element.
   */
  className?: string;
}

/**
 * Display contextual helper text with support for error states and custom styling.
 *
 * @param props - HelperText properties. See {@link HelperTextProps} for detailed prop documentation.
 * @returns Rendered helper text component
 *
 * @example
 * ```tsx
 * // Basic helper text
 * <HelperText id="email">Enter a valid email address</HelperText>
 *
 * // Error state helper text
 * <HelperText id="password" invalid>
 *   Password must be at least 8 characters
 * </HelperText>
 *
 * // Helper text with custom styling
 * <HelperText id="username" className="text-blue-500">
 *   Username must be unique
 * </HelperText>
 * ```
 */
const HelperText: React.FC<HelperTextProps> = ({
  id,
  children,
  invalid,
  className,
}) => {
  return (
    <span
      id={`helper-text-for-${id}`}
      className={cn(
        ['text-sm', 'text-foreground/50'],
        {
          'text-warning': invalid,
        },
        className,
      )}
    >
      {children}
    </span>
  );
};

export default HelperText;
